<template>
    <section id="checkout-personal-data">
        <div class="list-group-item collapse-element d-flex justify-content-between align-items-center">
            <b>{{$t('co.personal_data.variable-title', { steps: getSteps })}}</b>
            <b-button variant="text" v-if="!isVisible" @click="openCollapse">
                <font-awesome-icon icon="far fa-pencil" />
            </b-button>
        </div>
        <b-collapse
            class="list-group-item checkout-payment-box"
            tag="div"
            v-model="isVisible"
        >
            <div class="row" v-if="isLoading">
                <div class="col-12 my-3">
                    <b-skeleton height="1rem" width="96px" class="mb-3"></b-skeleton>
                    <b-skeleton height="1rem" width="230px" class="pt-2"></b-skeleton>
                </div>
                <div class="col-12 my-3">
                    <b-skeleton height="1rem" width="66px" class="mb-3"></b-skeleton>
                    <b-skeleton height="1rem" width="330px" class="pt-2"></b-skeleton>
                </div>
                <div class="col-12 col-md-6 mb-3">
                    <b-skeleton height="1rem" width="70px" class="mb-1"></b-skeleton>
                    <b-skeleton type="input" width="100%"></b-skeleton>
                </div>
                <div class="col-12 col-md-6 mb-3">
                    <b-skeleton height="1rem" width="70px" class="mb-1"></b-skeleton>
                    <b-skeleton type="input" width="100%"></b-skeleton>
                </div>
                <div class="col-12 col-md-6 mb-3">
                    <b-skeleton height="1rem" width="70px" class="mb-1"></b-skeleton>
                    <b-skeleton type="input" width="100%"></b-skeleton>
                </div>
                <div class="col-12 col-md-6 mb-3">
                    <b-skeleton height="1rem" width="70px" class="mb-1"></b-skeleton>
                    <b-skeleton type="input" width="100%"></b-skeleton>
                </div>
            </div>
            <div class="row" v-if="!isLoading">
                <div class="col-12 my-3" v-if="showBusinessCustomer">
                    <b-form-group
                        :invalid-feedback="fields.customerTypes.error"
                        :state="fields.customerTypes.state"
                    >
                        <label><b>{{$t('co.personal_data.customer_type.text')}}</b></label>
                        <b-form-radio-group inline v-model="fields.customerTypes.value">
                            <b-form-radio :value="0">{{ $t('co.personal_data.customer_type.private') }}</b-form-radio>
                            <b-form-radio :value="1">{{ $t('co.personal_data.customer_type.business') }}</b-form-radio>
                        </b-form-radio-group>
                    </b-form-group>
                </div>

                <div class="col-12 col-md-6 mb-3">
                    <b-form-group
                        :label="$t('co.personal_data.first_name')"
                        label-for="firstname"
                    >
                        <b-form-input
                            id="firstname"
                            :placeholder="$t('co.personal_data.first_name')"
                            type="text"
                            v-model="fields.firstname.value"
                            :state="fields.firstname.state"
                            aria-describedby="firstname-error"
                            tabindex="1"
                        ></b-form-input>
                        <b-form-invalid-feedback id="firstname-error">
                            {{ fields.firstname.error }}
                        </b-form-invalid-feedback>
                    </b-form-group>
                </div>
                <div class="col-12 col-md-6 mb-3">
                    <b-form-group
                        :label="$t('co.personal_data.last_name')"
                        label-for="lastname"
                    >
                        <b-form-input
                            id="lastname"
                            :placeholder="$t('co.personal_data.last_name')"
                            type="text"
                            v-model="fields.lastname.value"
                            :state="fields.lastname.state"
                            aria-describedby="lastname-error"
                            tabindex="2"
                        ></b-form-input>
                        <b-form-invalid-feedback id="lastname-error">
                            {{ fields.lastname.error }}
                        </b-form-invalid-feedback>
                    </b-form-group>
                </div>
                <div class="col-12 col-md-6 mb-3" v-if="ageVerificationNeeded">
                    <b-form-group
                        :label="$t('co.personal_data.birthday')"
                        label-for="birthday"
                        class="position-relative"
                    >
                      <div class="row">
                            <b-col cols="4" :order="this.$root.whitelabel.isUs() ? 2 : 1">
                                <b-form-input
                                    id="day"
                                    type="text"
                                    placeholder="DD"
                                    v-model="fields.birthdayDay.value"
                                    :class="{
                                        'is-invalid': fields.birthdayDay.state === false,
                                        'is-valid': fields.birthdayDay.state === true,
                                    }"
                                    class="form-control text-center"
                                    @input="formatDate(31, 'birthdayDay')"
                                    @change="formatDate(31, 'birthdayDay')"
                                    :tabindex="this.$root.whitelabel.isUs() ? 4 : 3"
                                />
                            </b-col>
                            <b-col cols="4" :order="this.$root.whitelabel.isUs() ? 1 : 2">
                                <b-form-input
                                    id="month"
                                    type="text"
                                    placeholder="MM"
                                    v-model="fields.birthdayMonth.value"
                                    :class="{
                                        'is-invalid': fields.birthdayMonth.state === false,
                                        'is-valid': fields.birthdayMonth.state === true,
                                    }"
                                    class="form-control text-center"
                                    @input="formatDate(12, 'birthdayMonth')"
                                    @change="formatDate(12, 'birthdayMonth')"
                                    :tabindex="this.$root.whitelabel.isUs() ? 3 : 4"
                                />
                            </b-col>
                            <b-col cols="4" order="3">
                                <b-form-input
                                    id="year"
                                    type="text"
                                    placeholder="YYYY"
                                    v-model="fields.birthdayYear.value"
                                    :class="{
                                        'is-invalid': fields.birthdayYear.state === false,
                                        'is-valid': fields.birthdayYear.state === true,
                                    }"
                                    class="form-control text-center"
                                    @input="formatDate(maxDate, 'birthdayYear')"
                                    @change="formatDate(maxDate, 'birthdayYear')"
                                    tabindex="5"
                                />
                            </b-col>
                        </div>
                        <small class="text-danger" v-if="fields.minAge.state === false">{{ $t('co.personal_data.ageError') }}</small>
                    </b-form-group>
                </div>
                <div class="col-12 col-md-6 mb-3">
                    <b-form-group
                        :label="$t('co.personal_data.mobile.phone.number')"
                        label-for="telephone"
                        v-if="this.$root.whitelabel.isUs()"
                    >
                        <b-form-input
                            id="telephone"
                            :placeholder="$t('co.personal_data.telephone.placeholder')"
                            type="text"
                            v-model="fields.telephone.value"
                            :state="fields.telephone.state"
                            aria-describedby="telephone-error"
                        ></b-form-input>
                        <div class="mt-2">{{ $t('co.personal_data.telephone.additionaltext') }}</div>
                        <b-form-invalid-feedback id="telephone-error">
                            {{ fields.telephone.error }}
                        </b-form-invalid-feedback>
                    </b-form-group>

                    <b-form-group
                        :label="$t('co.personal_data.telephone')"
                        label-for="telephone"
                        v-else
                    >
                        <b-form-input
                            id="telephone"
                            :placeholder="$t('co.personal_data.telephone.placeholder')"
                            type="text"
                            v-model="fields.telephone.value"
                            :state="fields.telephone.state"
                            aria-describedby="telephone-error"
                            @change="validatePhonenumber()"
                        ></b-form-input>
                        <b-form-invalid-feedback id="telephone-error">
                            {{ fields.telephone.error }}
                        </b-form-invalid-feedback>
                    </b-form-group>
                </div>

                <div class="col-12 mb-3" v-if="usesRefCodes">
                    <b-form-group
                        :label="$t('co.personal_data.referral_code')"
                        label-for="referral_code"
                    >
                        <b-form-input
                            id="referral_code"
                            :placeholder="$t('co.personal_data.referral_code')"
                            :disabled="user && user.referral_code !== null"
                            type="text"
                            v-model="fields.referral_code.value"
                            :state="fields.referral_code.state"
                            @input="validateRefCode"
                            aria-describedby="referral_code-error"
                        ></b-form-input>

                        <b-form-invalid-feedback id="ref-code-error">
                            {{ fields.referral_code.error }}
                        </b-form-invalid-feedback>

                    </b-form-group>
                </div>
            </div>
            <div class="row" v-if="fields.customerTypes.value == 1 && !isB2b">
                <div class="col-12 col-md-6 my-3">
                    <b-form-group
                        :label="$t('co.personal_data.company')"
                        label-for="company"
                    >
                        <b-form-input
                            id="company"
                            type="text"
                            :placeholder="$t('co.personal_data.company')"
                            v-model="fields.company.value"
                            :state="fields.company.state"
                            aria-describedby="company-error"
                        ></b-form-input>
                        <b-form-invalid-feedback id="company-error">
                            {{ fields.company.error }}
                        </b-form-invalid-feedback>
                    </b-form-group>
                </div>
                <div class="col-12 col-md-6 my-3">
                    <b-form-group
                        :label="$t('co.personal_data.company.type')"
                        label-for="company_legal_form"
                    >
                        <b-form-select
                            id="company_legal_form"
                            v-model="fields.company_legal_form.value"
                            :state="fields.company_legal_form.state"
                            aria-describedby="company-error"
                        >
                            <b-form-select-option
                                    v-for="option in fields.company_legal_form.options"
                                    :value="option.slug"
                                    :key="option.text"
                                >
                                {{ option.text }}
                            </b-form-select-option>
                        </b-form-select>
                        <b-form-invalid-feedback id="company-legal-form-error">
                            {{ fields.company_legal_form.error }}
                        </b-form-invalid-feedback>
                    </b-form-group>
                </div>
            </div>
            <div class="row" v-if="error">
                <div class="col-12"><span class="text-danger">{{$t('co.personal_data.error')}}</span></div>
            </div>
            <div class="row d-flex justify-content-end mt-3">
                <div class="col-12 d-flex justify-content-end">
                    <button type="button" class="btn btn-primary btn-mobile-full-width" @click="submit" v-if="!isLoading && !isSubmitting">
                        {{$t('co.button.continue')}}
                    </button>
                    <button type="button" class="btn btn-primary btn-mobile-full-width disabled" v-if="!isLoading && isSubmitting">
                        {{$t('co.button.loading')}}
                    </button>
                    <b-skeleton type="button" variant="primary" width="75px" v-if="isLoading"></b-skeleton>
                </div>
            </div>
        </b-collapse>
    </section>
</template>

<script>
import {
    BButton,
    BCollapse,
    BFormInput, BFormInvalidFeedback, BFormRadioGroup, BFormGroup, BFormSelect, BFormSelectOption, BFormRadio,
    BCol,
    BSkeleton
} from "bootstrap-vue"
import { EventBus } from '../../event-bus'
import moment from 'moment'
import { PhoneNumberUtil, PhoneNumberFormat } from 'google-libphonenumber';

function debounce(func, wait) {
    let timeout;
    return function(...args) {
        const context = this;
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(context, args), wait);
    };
}

export default {
    name: 'CheckoutPersonalData',
    components: {
        BButton,
        BCollapse,
        BFormInput, BFormInvalidFeedback, BFormRadioGroup, BFormGroup, BFormSelect, BFormSelectOption, BFormRadio,
        BCol,
        BSkeleton
    },
    props:{
        isLoading: true,
        user: null,
        numberOfSteps: {
            type: Number,
        },
    },
    data() {
        return {
            isVisible: true,
            isSubmitting: false,
            error: false,
            errorSum: 0,
            minYear: 0,
            showBusinessCustomer: this.$root.whitelabel.setting('show_business_customer'),
            fields: {
                minAge: {
                  value: 18,
                  state: null
                },
                customerTypes: {
                    value: '',
                    state: null,
                    error: this.$t('co.error.required_field'),
                },
                firstname: {
                    value: '',
                    state: null,
                    error: this.$t('co.error.required_field')
                },
                lastname: {
                    value: '',
                    state: null,
                    error: this.$t('co.error.required_field')
                },
                birthday: {
                  value: ''
                },
                birthdayDay: {
                    value: '',
                    state: null,
                    error: this.$t('co.error.required_field'),
                },
                birthdayMonth: {
                  value: '',
                  state: null,
                  error: 'Pflichtfeld',
                },
                birthdayYear: {
                  value: '',
                  state: null,
                  error: 'Pflichtfeld',
                },
                company: {
                    value: '',
                    state: null,
                    error: this.$t('co.error.required_field')
                },
                telephone: {
                    value: '',
                    state: null,
                    error: this.$t('co.error.invalid_phonenumber')
                },
                company_legal_form: {
                    value: '',
                    options: [
                        { slug: 'ag', text: this.$t('co.personal_data.company.type.ag')},
                        { slug: 'eg', text: this.$t('co.personal_data.company.type.eg')},
                        { slug: 'ek', text: this.$t('co.personal_data.company.type.ek')},
                        { slug: 'gmbh', text: this.$t('co.personal_data.company.type.gmbh')},
                        { slug: 'gmbh_ko_kg', text: this.$t('co.personal_data.company.type.gmbh_ko_kg')},
                        { slug: 'gbr', text: this.$t('co.personal_data.company.type.gbr')},
                        { slug: 'kg', text: this.$t('co.personal_data.company.type.kg')},
                        { slug: 'kgaa', text: this.$t('co.personal_data.company.type.kgaa')},
                        { slug: 'partg', text: this.$t('co.personal_data.company.type.partg')},
                        { slug: 'ug', text: this.$t('co.personal_data.company.type.ug')}
                    ],
                    state: null,
                    error: this.$t('co.error.required_field')
                },
                referral_code: {
                    value: null,
                    state: null,
                    error: this.$t('co.error.invalid_referral_code')
                },
            }
        }
    },
    mounted() {
        EventBus.$on('close-all-collapses', this.closeCollapse)
    },
    methods: {
        initFields(){
            const date = moment()
            this.minYear = date.subtract(18, 'years').year()

            this.fields.customerTypes.value = this.user.business_customer ? this.user.business_customer : 0
            this.fields.firstname.value = this.user.first_name ? this.user.first_name : ''
            this.fields.lastname.value = this.user.last_name ? this.user.last_name : ''
            if(this.ageVerificationNeeded && this.user.birthday){
                this.fields.birthdayDay.value = moment(this.user.birthday).format("DD")
                this.fields.birthdayMonth.value = moment(this.user.birthday).format("MM")
                this.fields.birthdayYear.value = moment(this.user.birthday).format("YYYY")
                
                this.validateAge()
            }
            this.fields.company.value = this.user.company_name ? this.user.company_name : null
            this.fields.company_legal_form.value = this.user.company_legal_form_slug ? this.user.company_legal_form_slug : null
            this.fields.telephone.value = this.user.telephone ? this.user.telephone : null
            this.fields.referral_code.value = this.user.referral_code ? this.user.referral_code : null;
            this.fields.referral_code.state = this.user.referral_code ? true : null;

            this.$root.checkoutReview.setPersonalData(this.fields)
            
        },
        validateForm(){
            this.errorSum = 0
            this.validateCustomerTypes()
            this.validateFirstname()
            this.validateLastname()
            if(this.ageVerificationNeeded){
                this.validateAge()
            }
            this.validatePhonenumber()
            this.validateCompany()
            this.validateCompanyLegalForm()
        },
        validateCustomerTypes(){
            this.fields.customerTypes.state = this.fields.customerTypes.value !== ''
            if(!this.fields.customerTypes.state){
                this.errorSum++
            }
        },
        validateFirstname(){
            this.fields.firstname.state = this.fields.firstname.value !== ''
            if(!this.fields.firstname.state){
                this.errorSum++
            }
        },
        validateLastname(){
            this.fields.lastname.state = this.fields.lastname.value !== ''
            if(!this.fields.lastname.state){
                this.errorSum++
            }
        },
        validateBirthday(){
          let birthdayErrorCounter = 0

          if(this.fields.birthdayDay.value !== '' && /^\d+$/.test(this.fields.birthdayDay.value) && this.fields.birthdayDay.value <= 31){
            this.fields.birthdayDay.state = true
          } else {
            this.fields.birthdayDay.state = false
            birthdayErrorCounter++
            this.errorSum++
          }
          if(this.fields.birthdayMonth.value !== '' && /^\d+$/.test(this.fields.birthdayMonth.value) && this.fields.birthdayMonth.value <= 12){
            this.fields.birthdayMonth.state = true
          } else {
            this.fields.birthdayMonth.state = false
            birthdayErrorCounter++
            this.errorSum++
          }

          const birthdayNotAllowedDates = ['30.02', '31.02', '31.04', '31.06', '31.09', '31.11']
          if (birthdayNotAllowedDates.find((element) => element === this.fields.birthdayDay.value + '.' + this.fields.birthdayMonth.value)) {
                this.fields.birthdayDay.state = false
                this.fields.birthdayMonth.state = false
                birthdayErrorCounter++
                this.errorSum++
          }

          if(this.fields.birthdayYear.value !== '' && /^\d+$/.test(this.fields.birthdayYear.value) && this.fields.birthdayYear.value <= this.minYear && this.fields.birthdayYear.value >= 1900){
            this.fields.birthdayYear.state = true
          } else {
            this.fields.birthdayYear.state = false
            birthdayErrorCounter++
            this.errorSum++
          }
        },
        validateAge(){
            this.fields.birthday.value = moment().set('date', this.fields.birthdayDay.value).set('months', this.fields.birthdayMonth.value - 1).set('year', this.fields.birthdayYear.value).format()
            const duration = moment().diff(this.fields.birthday.value, 'years')

            this.fields.minAge.state = duration >= 18

            if(this.fields.minAge.state === false){
                this.errorSum++
            } else {
                this.validateBirthday()
            }
        },
        validatePhonenumber(){
            let phone = this.fields.telephone.value
            let phoneUtil = PhoneNumberUtil.getInstance()
            try {
                let number = phone.trim().startsWith('+') ? phoneUtil.parse(phone) : phoneUtil.parse(phone, this.$root.whitelabel.setting('wlCountry'))

                if (phoneUtil.isValidNumber(number)) {
                    this.fields.telephone.value = phoneUtil.format(number, PhoneNumberFormat.INTERNATIONAL)
                    this.fields.telephone.state = true
                } else {
                    this.fields.telephone.state = false
                    this.errorSum++
                }
            } catch {
                this.fields.telephone.state = false
                this.errorSum++
            }
        },
        validateCompany(){
            if(this.fields.customerTypes.value === 1 && !this.isB2b) {
                this.fields.company.state = this.fields.company.value !== '' && this.fields.company.value !== null
                if(!this.fields.company.state){
                    this.errorSum++
                }
            }
        },
        validateCompanyLegalForm() {
            if(this.fields.customerTypes.value === 1 && !this.isB2b) {
                this.fields.company_legal_form.state = this.fields.company_legal_form.value !== null
                if(!this.fields.company_legal_form.state) {
                    this.errorSum++;
                }
            }
        },

        validateRefCode(value) {
            this.debouncedCheckCode(value);
        },

        submit(){
            this.validateForm()
            this.error = false

            if(this.errorSum === 0){
                this.isSubmitting = true
                this.$root.checkoutReview.setPersonalData(this.fields)
                this.$axios.put('api/v1/checkout/update_customer', {
                    uuid : this.$root.user.state.data.faaren_uuid,
                    subsidiary_uuid: this.$root.whitelabel.state.subid,
                    email : this.$root.user.state.data.email,
                    first_name : this.fields.firstname.value,
                    last_name : this.fields.lastname.value,
                    birthday : this.ageVerificationNeeded ? this.fields.birthday.value : null,
                    telephone : this.fields.telephone.value,
                    business_customer: this.$root.whitelabel.isB2b() ? "1" : this.fields.customerTypes.value,
                    company_name: this.fields.company.value,
                    company_legal_form_slug: this.fields.company_legal_form.value,
                    referral_code: this.fields.referral_code.state ? this.fields.referral_code.value : null

                }).then(response => {
                    this.isSubmitting = false
                    if(response.data){
                        this.isVisible = false
                        EventBus.$emit('checkout-open-step-2', null);
                        document.getElementById('account-data').scrollIntoView();
                    } else {
                        this.error = true
                    }
                }).catch(error => {
                    this.isSubmitting = false
                    this.error = true
                });
            }
        },
        formatDate(max, type){
          let d = this.fields[type].value
          d = d.toString().replace(/\D/g,'')

          if(d <= max){
            this.fields[type].value = d
          } else {
            this.fields[type].value = max
          }
        },
        openCollapse() {
            EventBus.$emit('close-all-collapses', null)
            this.$nextTick(() => {
                this.isVisible = true
            })
        },
        closeCollapse() {
            this.isVisible = false
        },
        debouncedCheckCode: debounce(function() {
            this.checkCode();
        }, 500),
        checkCode() {
            const code = this.fields.referral_code.value;
            this.$axios.get(`/api/v1/ref-code/check/${code}`).then(response => {
                this.fields.referral_code.state = response.data.valid;
            });
        },
    },
    watch: {
        user() {
            if(this.user !== null){
                this.initFields()
            }
        },

    },
    computed: {
        isB2b() {
            return this.$root.whitelabel.isB2b()
        },
        usesRefCodes() {
            return this.$root.whitelabel.usesRefCodesInCheckout();
        },
        maxDate(){
          return moment().subtract(18, 'years').year()
        },
        getSteps(){
            return this.numberOfSteps
        },
        ageVerificationNeeded(){
            return !this.$root.whitelabel.state.settings.noAgeVerification
        }
    }
}
</script>
